import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { AppProvider } from '@/context/AppContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { OfflineProvider } from '@/context/OfflineContext';
import { TimeProvider } from '@/context/TimeContext';
import { SettingsProvider } from '@/context/SettingsContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import POSPage from '@/pages/POSPage';
import TablesPage from '@/pages/TablesPage';
import OrdersPage from '@/pages/OrdersPage';
import KDSPage from '@/pages/KDSPage';
import MenuPage from '@/pages/MenuPage';
import ReportsPage from '@/pages/ReportsPage';
import UsersPage from '@/pages/UsersPage';
import InventoryPage from '@/pages/InventoryPage';
import CustomersPage from '@/pages/CustomersPage';
import SettingsPage from '@/pages/SettingsPage';
import QRMenuPage from '@/pages/QRMenuPage';
import OrderStatusPage from '@/pages/OrderStatusPage';
import TimesheetPage from '@/pages/TimesheetPage';
import AIGrowthPage from '@/pages/AIGrowthPage';

import './App.css';

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <OfflineProvider>
            <AppProvider>
              <TimeProvider>
              <SettingsProvider>
              <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/qr/:outletId" element={<QRMenuPage />} />
            <Route path="/o/:orderNumber" element={<OrderStatusPage />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout><DashboardPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/pos" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager', 'cashier', 'server']}>
                <Layout><POSPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/tables" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager', 'server']}>
                <Layout><TablesPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/orders" element={
              <ProtectedRoute>
                <Layout><OrdersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/kds" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager', 'kitchen_staff', 'expediter']}>
                <Layout><KDSPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/menu" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager']}>
                <Layout><MenuPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/inventory" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager', 'inventory_manager']}>
                <Layout><InventoryPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/customers" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager', 'cashier']}>
                <Layout><CustomersPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/timesheet" element={
              <ProtectedRoute>
                <Layout><TimesheetPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/ai" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager']}>
                <Layout><AIGrowthPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager']}>
                <Layout><ReportsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager']}>
                <Layout><UsersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'outlet_manager']}>
                <Layout><SettingsPage /></Layout>
              </ProtectedRoute>
            } />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster />
        </SettingsProvider>
        </TimeProvider>
        </AppProvider>
        </OfflineProvider>
      </AuthProvider>
    </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
